import React, { useState, useEffect } from "react";
import { Sidebar } from "../components/Sidebar";
import { useNavigate } from "@tanstack/react-router";
import {
  Search,
  Settings,
  Bell,
  Bot,
  Pause,
  BarChart3,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Plus,
  Filter,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  MessageSquare,
  Code,
  Shield,
  Globe,
  Database,
  Loader2,
  X,
  TestTube,
  Eye,
  EyeOff,
  Wifi,
  Server,
  Menu,
} from "lucide-react";
import { Button } from "../components/ui/button";

import { AgentConfigurationSummary } from "../components/AgentConfigurationSummary";

import { cn } from "../lib/utils";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

// Removed navigation props since TanStack Router handles navigation

interface AIAgent {
  id: string;
  name: string;
  description: string;
  status: "active" | "inactive" | "training" | "error";
  type: string;
  performance: {
    accuracy: number;
    responseTime: number;
    successRate: number;
    totalRequests: number;
  };
  lastActive: Date;
  version: string;
  model: string;
  tags: string[];
  category:
    | "conversational"
    | "data"
    | "safety"
    | "recommendation"
    | "language"
    | "development";
  // Agent creation workflow fields
  agentType?: string;
  useCase?: string;
  industry?: string;
  connectionType?: "mcp" | "rest-api";
  config?: {
    endpoint: string;
    apiKey: string;
    timeout: number;
    maxTokens: number;
    temperature: number;
    modelVersion: string;
    features: string[];
    serverUrl?: string;
    httpHeaders?: Array<{ key: string; value: string }>;
  };
  metrics?: {
    dailyRequests: number;
    averageResponseTime: number;
    errorRate: number;
    uptime: number;
    costPerRequest: number;
    totalCost: number;
  };
  testResults?: {
    totalTests: number;
    passedTests: number;
    failedTests: number;
    lastTestRun: Date;
    averageScore: number;
  };
  testRuns?: Array<{
    id: string;
    name: string;
    status: "passed" | "failed" | "running" | "pending";
    startTime: Date;
    endTime?: Date;
    duration?: number;
    totalTests: number;
    passedTests: number;
    failedTests: number;
    score: number;
    triggeredBy: string;
    environment: string;
  }>;
}

interface AgentConnectionType {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  type: "mcp" | "rest-api";
}

interface AgentForm {
  name: string;
  description: string;
  endpoint: string;
  apiKey: string;
  model: string;
  timeout: number;
  maxTokens: number;
  temperature: number;
  // MCP specific fields
  serverUrl?: string;
  httpHeaders?: Array<{ key: string; value: string }>;
}

// Custom hook for session-based agent storage
const useAgentStorage = () => {
  const [agents, setAgents] = useState<AIAgent[]>(sampleAgents);
  const [isLoaded, setIsLoaded] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Load agents from sessionStorage on mount
  useEffect(() => {
    try {
      const storedAgents = sessionStorage.getItem("ai-agents");
      if (storedAgents) {
        const parsedAgents = JSON.parse(storedAgents).map((agent: any) => ({
          ...agent,
          lastActive: new Date(agent.lastActive),
        }));
        setAgents(parsedAgents);
      }
    } catch (error) {
      console.error("Error loading agents from sessionStorage:", error);
      // Keep the default sampleAgents
    }
    setIsLoaded(true);
  }, []);

  // Save agents to sessionStorage whenever agents change
  useEffect(() => {
    if (isLoaded) {
      try {
        sessionStorage.setItem("ai-agents", JSON.stringify(agents));
        setLastUpdated(new Date());
      } catch (error) {
        console.error("Error saving agents to sessionStorage:", error);
      }
    }
  }, [agents, isLoaded]);

  const addAgent = (newAgent: AIAgent) => {
    setAgents((prev) => [newAgent, ...prev]);
  };

  const updateAgent = (agentId: string, updates: Partial<AIAgent>) => {
    setAgents((prev) =>
      prev.map((agent) =>
        agent.id === agentId ? { ...agent, ...updates } : agent,
      ),
    );
  };

  const removeAgent = (agentId: string) => {
    setAgents((prev) => prev.filter((agent) => agent.id !== agentId));
  };

  const clearAgents = () => {
    setAgents([]);
    sessionStorage.removeItem("ai-agents");
  };

  const resetToSampleAgents = () => {
    setAgents(sampleAgents);
  };

  return {
    agents,
    addAgent,
    updateAgent,
    removeAgent,
    clearAgents,
    resetToSampleAgents,
    isLoaded,
    lastUpdated,
  };
};

// Utility to format time difference as 'X minutes ago', 'X hours ago', etc.
function formatRelativeTime(date: Date, now: Date) {
  const diff = now.getTime() - date.getTime();
  const absDiff = Math.abs(diff);

  if (absDiff < 60 * 1000) return "Real-time";
  if (absDiff < 60 * 60 * 1000) {
    const mins = Math.round(absDiff / (60 * 1000));
    return `${mins} minute${mins > 1 ? "s" : ""} ago`;
  }
  if (absDiff < 24 * 60 * 60 * 1000) {
    const hours = Math.round(absDiff / (60 * 60 * 1000));
    return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  }
  const days = Math.round(absDiff / (24 * 60 * 60 * 1000));
  return `${days} day${days > 1 ? "s" : ""} ago`;
}

const now = new Date();
const minutesAgo = (n: number) => new Date(now.getTime() - n * 60 * 1000);
const hoursAgo = (n: number) => new Date(now.getTime() - n * 60 * 60 * 1000);

export const sampleAgents: AIAgent[] = [
  {
    id: "1",
    name: "Beauty Sales Agent",
    description:
      "AI agent for luxury cosmetics sales and customer consultation",
    status: "active",
    type: "Sales AI",
    performance: {
      accuracy: 85.2,
      responseTime: 2.1,
      successRate: 78.5,
      totalRequests: 8920,
    },
    lastActive: minutesAgo(2),
    version: "v2.1.3",
    model: "GPT-4",
    tags: ["sales", "luxury", "cosmetics", "customer-service"],
    category: "conversational",
    // New fields from creation workflow
    agentType: "sales",
    useCase: "product_recommendations",
    industry: "ecommerce",
    connectionType: "rest-api",
    config: {
      endpoint: "https://api.Beauty.com/v1/chat",
      apiKey: "sk-...",
      timeout: 30000,
      maxTokens: 4000,
      temperature: 0.7,
      modelVersion: "GPT-4",
      features: ["product_knowledge", "personalization"],
    },
    metrics: {
      dailyRequests: 150,
      averageResponseTime: 2.1,
      errorRate: 0.5,
      uptime: 99.8,
      costPerRequest: 0.02,
      totalCost: 178.4,
    },
    testResults: {
      totalTests: 45,
      passedTests: 42,
      failedTests: 3,
      lastTestRun: minutesAgo(30),
      averageScore: 93.3,
    },
    testRuns: [],
  },
];

const connectionTypes: AgentConnectionType[] = [
  {
    id: "mcp",
    name: "Model Context Protocol (MCP)",
    description: "Connect to AI agents using the Model Context Protocol",
    icon: <Server className="h-8 w-8 text-white" />,
    type: "mcp",
  },
  {
    id: "rest-api",
    name: "REST API",
    description: "Connect to AI agents via REST API endpoints",
    icon: <Wifi className="h-8 w-8 text-white" />,
    type: "rest-api",
  },
];

interface TestAgentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (agent: AIAgent) => void;
}

// Enhanced agent creation workflow
const TestAgentModal: React.FC<TestAgentModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [step, setStep] = useState(1);
  const [selectedConnection, setSelectedConnection] =
    useState<AgentConnectionType | null>(null);
  const [agentType, setAgentType] = useState<string>("");
  const [useCase, setUseCase] = useState<string>("");
  const [industry, setIndustry] = useState<string>("");
  const [testFocus, setTestFocus] = useState<string[]>([]);
  const [form, setForm] = useState<AgentForm>({
    name: "",
    description: "",
    endpoint: "",
    apiKey: "",
    model: "GPT-4",
    timeout: 30000,
    maxTokens: 4000,
    temperature: 0.7,
    serverUrl: "",
    httpHeaders: [],
  });
  const [showApiKey, setShowApiKey] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<"success" | "error" | null>(
    null,
  );
  const [testMessage, setTestMessage] = useState("");

  // Agent type options with descriptions
  const agentTypes = [
    {
      id: "conversational",
      name: "Conversational AI",
      description: "Chatbots, customer service, virtual assistants",
      icon: "💬",
      testFocus: [
        "response_accuracy",
        "conversation_flow",
        "context_understanding",
        "personality_consistency",
      ],
    },
    {
      id: "sales",
      name: "Sales & Marketing",
      description:
        "Lead generation, product recommendations, conversion optimization",
      icon: "💰",
      testFocus: [
        "conversion_rate",
        "product_knowledge",
        "personalization",
        "sales_techniques",
      ],
    },
    {
      id: "content",
      name: "Content Generation",
      description: "Writing, creative content, document generation",
      icon: "✍️",
      testFocus: [
        "content_quality",
        "creativity",
        "factual_accuracy",
        "style_consistency",
      ],
    },
    {
      id: "analytics",
      name: "Data & Analytics",
      description: "Data analysis, insights, reporting",
      icon: "📊",
      testFocus: [
        "data_accuracy",
        "insight_relevance",
        "calculation_precision",
        "visualization_quality",
      ],
    },
    {
      id: "safety",
      name: "Safety & Compliance",
      description: "Content moderation, safety checks, regulatory compliance",
      icon: "🛡️",
      testFocus: [
        "safety_checks",
        "compliance_accuracy",
        "bias_detection",
        "harmful_content_filtering",
      ],
    },
    {
      id: "development",
      name: "Development Assistant",
      description: "Code generation, debugging, technical support",
      icon: "💻",
      testFocus: [
        "code_quality",
        "bug_detection",
        "best_practices",
        "technical_accuracy",
      ],
    },
  ];

  // Industry options
  const industries = [
    { id: "ecommerce", name: "E-commerce & Retail", icon: "🛒" },
    { id: "healthcare", name: "Healthcare & Medical", icon: "🏥" },
    { id: "finance", name: "Finance & Banking", icon: "🏦" },
    { id: "education", name: "Education & Training", icon: "🎓" },
    { id: "technology", name: "Technology & SaaS", icon: "💻" },
    { id: "media", name: "Media & Entertainment", icon: "🎬" },
    {
      id: "consulting",
      name: "Consulting & Professional Services",
      icon: "📋",
    },
    { id: "manufacturing", name: "Manufacturing & Industrial", icon: "🏭" },
    { id: "other", name: "Other", icon: "🔧" },
  ];

  // Use case options based on agent type
  const getUseCases = (type: string) => {
    const useCaseMap: {
      [key: string]: Array<{ id: string; name: string; description: string }>;
    } = {
      conversational: [
        {
          id: "customer_service",
          name: "Customer Service",
          description: "Handle customer inquiries and support",
        },
        {
          id: "virtual_assistant",
          name: "Virtual Assistant",
          description: "Personal assistant and task management",
        },
        {
          id: "chatbot",
          name: "Chatbot",
          description: "General conversation and engagement",
        },
      ],
      sales: [
        {
          id: "lead_generation",
          name: "Lead Generation",
          description: "Identify and qualify potential customers",
        },
        {
          id: "product_recommendations",
          name: "Product Recommendations",
          description: "Suggest products based on preferences",
        },
        {
          id: "sales_conversation",
          name: "Sales Conversation",
          description: "Guide customers through purchase process",
        },
      ],
      content: [
        {
          id: "content_writing",
          name: "Content Writing",
          description: "Create articles, blogs, and marketing copy",
        },
        {
          id: "creative_writing",
          name: "Creative Writing",
          description: "Generate stories, scripts, and creative content",
        },
        {
          id: "documentation",
          name: "Documentation",
          description: "Create technical and business documentation",
        },
      ],
      analytics: [
        {
          id: "data_analysis",
          name: "Data Analysis",
          description: "Analyze data and generate insights",
        },
        {
          id: "reporting",
          name: "Reporting",
          description: "Create automated reports and dashboards",
        },
        {
          id: "predictive_analytics",
          name: "Predictive Analytics",
          description: "Forecast trends and outcomes",
        },
      ],
      safety: [
        {
          id: "content_moderation",
          name: "Content Moderation",
          description: "Filter inappropriate or harmful content",
        },
        {
          id: "compliance_checking",
          name: "Compliance Checking",
          description: "Ensure regulatory and policy compliance",
        },
        {
          id: "bias_detection",
          name: "Bias Detection",
          description: "Identify and mitigate bias in content",
        },
      ],
      development: [
        {
          id: "code_generation",
          name: "Code Generation",
          description: "Generate code snippets and functions",
        },
        {
          id: "code_review",
          name: "Code Review",
          description: "Review and suggest improvements to code",
        },
        {
          id: "debugging",
          name: "Debugging",
          description: "Help identify and fix code issues",
        },
      ],
    };
    return useCaseMap[type] || [];
  };

  const resetModal = () => {
    setStep(1);
    setSelectedConnection(null);
    setAgentType("");
    setUseCase("");
    setIndustry("");
    setTestFocus([]);
    setForm({
      name: "",
      description: "",
      endpoint: "",
      apiKey: "",
      model: "GPT-4",
      timeout: 30000,
      maxTokens: 4000,
      temperature: 0.7,
      serverUrl: "",
      httpHeaders: [],
    });
    setShowApiKey(false);
    setIsTesting(false);
    setTestResult(null);
    setTestMessage("");
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const handleAgentTypeSelect = (type: string) => {
    setAgentType(type);
    setTestFocus(agentTypes.find((t) => t.id === type)?.testFocus || []);
    setStep(2);
  };

  const handleUseCaseSelect = (useCaseId: string) => {
    setUseCase(useCaseId);
    setStep(3);
  };

  const handleIndustrySelect = (industryId: string) => {
    setIndustry(industryId);
    setStep(4);
  };

  const handleConnectionSelect = (connection: AgentConnectionType) => {
    setSelectedConnection(connection);
    setStep(5);
  };

  const handleTestConnection = async () => {
    setIsTesting(true);
    setTestResult(null);
    setTestMessage("");

    await new Promise((resolve) => setTimeout(resolve, 2000));

    if (selectedConnection?.type === "mcp") {
      if (form.serverUrl) {
        setTestResult("success");
        setTestMessage(
          "MCP server connection successful! Agent is ready to use.",
        );
      } else {
        setTestResult("error");
        setTestMessage("MCP connection failed. Please check your server URL.");
      }
    } else {
      if (form.endpoint && form.apiKey) {
        setTestResult("success");
        setTestMessage(
          "REST API connection successful! Agent is ready to use.",
        );
      } else {
        setTestResult("error");
        setTestMessage(
          "Connection failed. Please check your endpoint and API key.",
        );
      }
    }

    setIsTesting(false);
  };

  const handleFinish = () => {
    if (selectedConnection && form.name) {
      const newAgent: AIAgent = {
        id: Date.now().toString(),
        name: form.name,
        description: form.description,
        status: "active",
        type: `${selectedConnection.name} Agent`,
        performance: {
          accuracy: 0,
          responseTime: 0,
          successRate: 0,
          totalRequests: 0,
        },
        lastActive: new Date(),
        version: "v1.0.0",
        model: form.model,
        tags: [selectedConnection.type, agentType, useCase, industry],
        category: agentType as any,
        // New fields from creation workflow
        agentType: agentType,
        useCase: useCase,
        industry: industry,
        connectionType: selectedConnection.type as "mcp" | "rest-api",
        config: {
          endpoint: form.endpoint,
          apiKey: form.apiKey,
          timeout: form.timeout,
          maxTokens: form.maxTokens,
          temperature: form.temperature,
          modelVersion: form.model,
          features: [],
          serverUrl: form.serverUrl,
          httpHeaders: form.httpHeaders,
        },
        metrics: {
          dailyRequests: 0,
          averageResponseTime: 0,
          errorRate: 0,
          uptime: 0,
          costPerRequest: 0,
          totalCost: 0,
        },
        testResults: {
          totalTests: 0,
          passedTests: 0,
          failedTests: 0,
          lastTestRun: new Date(),
          averageScore: 0,
        },
        testRuns: [],
      };
      onSuccess(newAgent);
      handleClose();
    }
  };

  const addHttpHeader = () => {
    setForm({
      ...form,
      httpHeaders: [...(form.httpHeaders || []), { key: "", value: "" }],
    });
  };

  const removeHttpHeader = (index: number) => {
    const newHeaders = [...(form.httpHeaders || [])];
    newHeaders.splice(index, 1);
    setForm({ ...form, httpHeaders: newHeaders });
  };

  const updateHttpHeader = (
    index: number,
    field: "key" | "value",
    value: string,
  ) => {
    const newHeaders = [...(form.httpHeaders || [])];
    newHeaders[index] = { ...newHeaders[index], [field]: value };
    setForm({ ...form, httpHeaders: newHeaders });
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-gray-800 border-gray-700 text-gray-100 min-w-[90vh] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-gray-100">
            Test AI Agent
          </DialogTitle>
        </DialogHeader>

        {/* Enhanced Progress Steps */}
        <div className="px-6 py-4 border-b border-gray-700">
          <div className="flex items-center justify-center">
            {[1, 2, 3, 4, 5, 6, 7].map((stepNumber, idx, arr) => (
              <React.Fragment key={stepNumber}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      step >= stepNumber
                        ? "bg-pink-400 text-white"
                        : "bg-gray-700 text-gray-400"
                    }`}
                  >
                    {stepNumber}
                  </div>
                </div>
                {idx < arr.length - 1 && (
                  <div
                    className={`w-8 h-0.5 mx-2 ${
                      step > stepNumber ? "bg-pink-400" : "bg-gray-700"
                    }`}
                    style={{ alignSelf: "center" }}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
          <div className="flex items-center justify-center mt-2 gap-x-8">
            <span className="text-xs text-gray-400 w-12 text-center">Type</span>
            <span className="text-xs text-gray-400 w-12 text-center">
              Use Case
            </span>
            <span className="text-xs text-gray-400 w-12 text-center">
              Industry
            </span>
            <span className="text-xs text-gray-400 w-12 text-center">
              Connect
            </span>
            <span className="text-xs text-gray-400 w-12 text-center">
              Configure
            </span>
            <span className="text-xs text-gray-400 w-12 text-center">Test</span>
            <span className="text-xs text-gray-400 w-12 text-center">Done</span>
          </div>
        </div>

        {/* Step 1: Choose Agent Type */}
        {step === 1 && (
          <div className="p-6">
            <div className="text-center mb-6">
              <h3 className="text-lg font-medium text-gray-100 mb-2">
                What type of AI agent are you testing?
              </h3>
              <p className="text-sm text-gray-400">
                This helps us understand your agent's purpose and generate
                relevant tests
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {agentTypes.map((type) => (
                <div
                  key={type.id}
                  onClick={() => handleAgentTypeSelect(type.id)}
                  className="p-4 border border-gray-700 rounded-lg hover:border-pink-500 hover:bg-gray-750 cursor-pointer transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">{type.icon}</span>
                    <div>
                      <h4 className="font-medium text-gray-100">{type.name}</h4>
                      <p className="text-sm text-gray-400">
                        {type.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Choose Use Case */}
        {step === 2 && (
          <div className="p-6">
            <div className="text-center mb-6">
              <h3 className="text-lg font-medium text-gray-100 mb-2">
                What's the primary use case for your agent?
              </h3>
              <p className="text-sm text-gray-400">
                This helps us tailor the testing approach to your specific needs
              </p>
            </div>
            <div className="grid grid-cols-1 gap-3">
              {getUseCases(agentType).map((useCase) => (
                <div
                  key={useCase.id}
                  onClick={() => handleUseCaseSelect(useCase.id)}
                  className="p-4 border border-gray-700 rounded-lg hover:border-pink-500 hover:bg-gray-750 cursor-pointer transition-colors"
                >
                  <div>
                    <h4 className="font-medium text-gray-100">
                      {useCase.name}
                    </h4>
                    <p className="text-sm text-gray-400">
                      {useCase.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-6">
              <Button
                variant="outline"
                onClick={() => setStep(1)}
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                Back
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Choose Industry */}
        {step === 3 && (
          <div className="p-6">
            <div className="text-center mb-6">
              <h3 className="text-lg font-medium text-gray-100 mb-2">
                What industry does your agent operate in?
              </h3>
              <p className="text-sm text-gray-400">
                This helps us apply industry-specific testing standards and
                compliance requirements
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {industries.map((industry) => (
                <div
                  key={industry.id}
                  onClick={() => handleIndustrySelect(industry.id)}
                  className="p-4 border border-gray-700 rounded-lg hover:border-pink-500 hover:bg-gray-750 cursor-pointer transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">{industry.icon}</span>
                    <div>
                      <h4 className="font-medium text-gray-100">
                        {industry.name}
                      </h4>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-6">
              <Button
                variant="outline"
                onClick={() => setStep(2)}
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                Back
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Choose Connection Type */}
        {step === 4 && (
          <div className="p-6">
            <div className="text-center mb-6">
              <h3 className="text-lg font-medium text-gray-100 mb-2">
                How does your agent connect?
              </h3>
              <p className="text-sm text-gray-400">
                Choose the connection method for your AI agent
              </p>
            </div>
            <div className="grid grid-cols-1 gap-4">
              {connectionTypes.map((connection) => (
                <div
                  key={connection.id}
                  onClick={() => handleConnectionSelect(connection)}
                  className="p-4 border border-gray-700 rounded-lg hover:border-pink-500 hover:bg-gray-750 cursor-pointer transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    {connection.icon}
                    <div>
                      <h4 className="font-medium text-gray-100">
                        {connection.name}
                      </h4>
                      <p className="text-sm text-gray-400">
                        {connection.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-6">
              <Button
                variant="outline"
                onClick={() => setStep(3)}
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                Back
              </Button>
            </div>
          </div>
        )}

        {/* Step 5: Configure Agent */}
        {step === 5 && selectedConnection && (
          <div className="p-6">
            <div className="flex items-center space-x-3 mb-6">
              {selectedConnection.icon}
              <div>
                <h3 className="text-lg font-medium text-gray-100">
                  Configure Agent for Testing
                </h3>
                <p className="text-sm text-gray-400">
                  Enter your agent's connection details for testing
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Agent Name
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:ring-2 focus:ring-pink-500"
                  placeholder="My AI Agent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Test Description
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                  rows={3}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:ring-2 focus:ring-pink-500"
                  placeholder="Describe what aspects of your agent you want to test..."
                />
              </div>

              {/* MCP Server Configuration */}
              {selectedConnection.type === "mcp" && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Server URL
                    </label>
                    <input
                      type="url"
                      value={form.serverUrl || ""}
                      onChange={(e) =>
                        setForm({ ...form, serverUrl: e.target.value })
                      }
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:ring-2 focus:ring-pink-500"
                      placeholder="https://mcp-server.example.com"
                    />
                  </div>

                  <div className="mt-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-300">
                        HTTP Headers
                      </label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addHttpHeader}
                        className="text-xs border-gray-600 text-gray-300 hover:bg-gray-700"
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Add Header
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {(form.httpHeaders || []).map((header, index) => (
                        <div key={index} className="flex space-x-2">
                          <input
                            type="text"
                            value={header.key}
                            onChange={(e) =>
                              updateHttpHeader(index, "key", e.target.value)
                            }
                            placeholder="Header name"
                            className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:ring-2 focus:ring-pink-500"
                          />
                          <input
                            type="text"
                            value={header.value}
                            onChange={(e) =>
                              updateHttpHeader(index, "value", e.target.value)
                            }
                            placeholder="Header value"
                            className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:ring-2 focus:ring-pink-500"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => removeHttpHeader(index)}
                            className="text-xs border-gray-600 text-gray-300 hover:bg-gray-700"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                      {(form.httpHeaders || []).length === 0 && (
                        <div className="text-xs text-gray-500 italic">
                          No headers added. Click "Add Header" to add custom
                          HTTP headers.
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* REST API Configuration */}
              {selectedConnection.type === "rest-api" && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Endpoint URL
                    </label>
                    <input
                      type="url"
                      value={form.endpoint}
                      onChange={(e) =>
                        setForm({ ...form, endpoint: e.target.value })
                      }
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:ring-2 focus:ring-pink-500"
                      placeholder="https://api.example.com/v1/chat"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      API Key
                    </label>
                    <div className="relative">
                      <input
                        type={showApiKey ? "text" : "password"}
                        value={form.apiKey}
                        onChange={(e) =>
                          setForm({ ...form, apiKey: e.target.value })
                        }
                        className="w-full px-3 py-2 pr-10 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:ring-2 focus:ring-pink-500"
                        placeholder="Enter your API key"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowApiKey(!showApiKey)}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 hover:bg-gray-600"
                      >
                        {showApiKey ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="flex justify-between mt-6">
              <Button
                variant="outline"
                onClick={() => setStep(4)}
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                Back
              </Button>
              <Button
                onClick={() => setStep(6)}
                disabled={
                  !form.name ||
                  (selectedConnection.type === "mcp"
                    ? !form.serverUrl
                    : !form.endpoint || !form.apiKey)
                }
                className="bg-pink-400 hover:bg-pink-500 disabled:opacity-50"
              >
                Next
              </Button>
            </div>
          </div>
        )}

        {/* Step 6: Test Connection */}
        {step === 6 && (
          <div className="p-6">
            <div className="flex items-center space-x-3 mb-6">
              <TestTube className="h-6 w-6 text-pink-400" />
              <div>
                <h3 className="text-lg font-medium text-gray-100">
                  Test Agent Connection
                </h3>
                <p className="text-sm text-gray-400">
                  Verify your agent is accessible for testing
                </p>
              </div>
            </div>

            <div className="bg-gray-750 rounded-lg p-4 mb-6">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Name:</span>
                  <span className="text-gray-100">{form.name}</span>
                </div>
                {selectedConnection?.type === "mcp" ? (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Server URL:</span>
                      <span className="text-gray-100">{form.serverUrl}</span>
                    </div>
                    {form.httpHeaders && form.httpHeaders.length > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">HTTP Headers:</span>
                        <span className="text-gray-100">
                          {form.httpHeaders.length} headers
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Endpoint:</span>
                      <span className="text-gray-100">{form.endpoint}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">API Key:</span>
                      <span className="text-gray-100">
                        {form.apiKey ? "••••••••" : "Not provided"}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {testResult && (
              <div className="mb-6 p-4 rounded-lg bg-gray-750">
                <div className="flex items-center space-x-2">
                  {testResult === "success" ? (
                    <CheckCircle className="h-5 w-5 text-green-400" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-400" />
                  )}
                  <span
                    className={
                      testResult === "success"
                        ? "text-green-400"
                        : "text-red-400"
                    }
                  >
                    {testMessage}
                  </span>
                </div>
              </div>
            )}

            <div className="flex justify-between">
              <Button
                variant="outline"
                onClick={() => setStep(5)}
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                Back
              </Button>
              <div className="flex space-x-2">
                <Button
                  onClick={handleTestConnection}
                  disabled={isTesting}
                  className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                >
                  {isTesting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Testing...
                    </>
                  ) : (
                    <>
                      <TestTube className="h-4 w-4 mr-2" />
                      Test Connection
                    </>
                  )}
                </Button>
                {testResult === "success" && (
                  <Button
                    onClick={() => setStep(7)}
                    className="bg-pink-400 hover:bg-pink-500"
                  >
                    Continue
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Step 7: Configuration Summary */}
        {step === 7 && (
          <div className="p-6">
            <AgentConfigurationSummary
              agentName={form.name}
              agentType={agentType}
              useCase={useCase}
              industry={industry}
              connectionType={selectedConnection?.name || ""}
              testCount={0}
              complianceRequired={false}
            />

            <div className="flex justify-center mt-6">
              <Button
                onClick={handleFinish}
                className="bg-pink-400 hover:bg-pink-500"
              >
                Start Testing
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const AgentRow: React.FC<{ agent: AIAgent }> = ({ agent }) => {
  const navigate = useNavigate();
  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-pink-900/30 text-pink-400 border-pink-600/30";
      case "inactive":
        return "bg-gray-900/30 text-gray-400 border-gray-600/30";
      case "training":
        return "bg-blue-900/30 text-blue-400 border-blue-600/30";
      case "error":
        return "bg-red-900/30 text-red-400 border-red-600/30";
      default:
        return "bg-gray-900/30 text-gray-400 border-gray-600/30";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "active":
        return <CheckCircle className="h-4 w-4" />;
      case "inactive":
        return <Pause className="h-4 w-4" />;
      case "training":
        return <Loader2 className="h-4 w-4 animate-spin" />;
      case "error":
        return <XCircle className="h-4 w-4" />;
      default:
        return <AlertTriangle className="h-4 w-4" />;
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case "conversational":
        return <MessageSquare className="h-3 w-3" />;
      case "data":
        return <Database className="h-3 w-3" />;
      case "safety":
        return <Shield className="h-3 w-3" />;
      case "recommendation":
        return <BarChart3 className="h-3 w-3" />;
      case "language":
        return <Globe className="h-3 w-3" />;
      case "development":
        return <Code className="h-3 w-3" />;
      default:
        return <Bot className="h-3 w-3" />;
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case "conversational":
        return "Conversational";
      case "data":
        return "Data";
      case "safety":
        return "Safety";
      case "recommendation":
        return "Recommendation";
      case "language":
        return "Language";
      case "development":
        return "Development";
      default:
        return "Other";
    }
  };

  return (
    <div
      className="flex items-center p-4 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-750 transition-colors cursor-pointer"
      onClick={() =>
        navigate({
          to: "/ai-agents/$agentId",
          params: { agentId: agent.id },
        })
      }
    >
      {/* Left: Status, Avatar, Name, Description, Category */}
      <div className="flex items-center space-x-4 flex-1 min-w-0">
        <div className="flex items-center space-x-3 min-w-0">
          <Avatar className="h-8 w-8">
            <AvatarFallback className="bg-blue-600 text-blue-100">
              <Bot className="h-4 w-4" />
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <h3 className="text-sm font-medium text-gray-100 truncate">
              {agent.name}
            </h3>
            <p className="text-xs text-gray-400 truncate">
              {agent.description}
            </p>
          </div>
        </div>
      </div>

      {/* Right: Metrics, Model/Version, Last Active, Actions */}
      <div className="flex items-center space-x-4 ml-auto">
        <div className="flex items-center space-x-2 text-xs">
          <Badge
            variant="secondary"
            className="bg-green-900/30 text-green-400 border-green-600/30"
          >
            {agent.performance.accuracy}% accuracy
          </Badge>
          <Badge
            variant="secondary"
            className="bg-blue-900/30 text-blue-400 border-blue-600/30"
          >
            {agent.performance.successRate}% success
          </Badge>
        </div>
      </div>
    </div>
  );
};

export const AIAgentsPage: React.FC = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  const {
    agents,
    addAgent,
    updateAgent,
    removeAgent,
    clearAgents,
    resetToSampleAgents,
    isLoaded,
    lastUpdated,
  } = useAgentStorage();
  const itemsPerPage = 10;

  // Filter agents based on current filters
  let filteredAgents = agents.filter((agent) => {
    const matchesStatus =
      statusFilter === "all" || agent.status === statusFilter;
    const matchesCategory =
      categoryFilter === "all" || agent.category === categoryFilter;
    const matchesSearch =
      searchQuery === "" ||
      agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      agent.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      agent.type.toLowerCase().includes(searchQuery.toLowerCase()) ||
      agent.model.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesStatus && matchesCategory && matchesSearch;
  });

  // Sort by last active (most recent first)
  filteredAgents = filteredAgents.sort(
    (a, b) => b.lastActive.getTime() - a.lastActive.getTime(),
  );

  // Calculate pagination
  const totalPages = Math.ceil(filteredAgents.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentAgents = filteredAgents.slice(startIndex, endIndex);

  const handleAddAgent = (newAgent: AIAgent) => {
    addAgent(newAgent);
  };

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  // Show loading state while agents are being loaded
  if (!isLoaded) {
    return (
      <div className="flex h-screen bg-gray-900 items-center justify-center">
        <div className="flex items-center space-x-3">
          <Loader2 className="h-6 w-6 animate-spin text-pink-400" />
          <span className="text-gray-300">Loading agents...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-900">
      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          "fixed lg:relative z-50 h-full transition-transform duration-300 ease-in-out",
          isSidebarOpen
            ? "translate-x-0"
            : "-translate-x-full lg:translate-x-0",
        )}
      >
        <Sidebar />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col h-full min-h-0 overflow-hidden">
        {/* Top Header */}
        <header className="bg-gray-800 border-b border-gray-700 px-4 lg:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* Mobile Menu Button */}
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden text-gray-300 hover:bg-gray-700"
                onClick={toggleSidebar}
              >
                <Menu className="h-5 w-5" />
              </Button>

              <h1 className="text-xl lg:text-2xl font-bold text-gray-100">
                AI Agents
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <Button
                className="flex items-center space-x-2 bg-pink-400 hover:bg-pink-500"
                onClick={() => setIsTestModalOpen(true)}
              >
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">Test Agent</span>
              </Button>
              <Button
                variant="default"
                size="icon"
                className="bg-gray-700 hover:bg-gray-600 text-gray-300"
              >
                <Bell className="h-5 w-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="text-gray-300 hover:bg-gray-700"
              >
                <Settings className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </header>

        {/* Filters */}
        <div className="bg-gray-800 border-b border-gray-700 px-4 lg:px-6 py-3">
          <div className="flex flex-col lg:flex-row lg:items-center space-y-3 lg:space-y-0 lg:space-x-4">
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <span className="text-sm text-gray-300">Filters:</span>
            </div>

            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-1 bg-gray-700 border border-gray-600 rounded-md text-sm text-gray-100 focus:outline-none focus:ring-2 focus:ring-pink-500"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="training">Training</option>
                <option value="error">Error</option>
              </select>

              {/* Category Filter */}
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="px-3 py-1 bg-gray-700 border border-gray-600 rounded-md text-sm text-gray-100 focus:outline-none focus:ring-2 focus:ring-pink-500"
              >
                <option value="all">All Categories</option>
                <option value="conversational">Conversational</option>
                <option value="data">Data</option>
                <option value="safety">Safety</option>
                <option value="recommendation">Recommendation</option>
                <option value="language">Language</option>
                <option value="development">Development</option>
              </select>
            </div>

            <span className="text-sm text-gray-400">
              {filteredAgents.length} of {agents.length} agents
            </span>
            <div className="relative lg:ml-auto">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search agents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-1 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-gray-100 placeholder-gray-400 w-full lg:w-auto"
              />
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <main className="flex-1 h-full flex flex-col min-h-0 bg-gray-900">
          {/* Agents List with Pagination */}
          <div className="h-full flex flex-col min-h-0">
            {/* Scrollable content area */}
            <div className="flex-1 p-4 lg:p-6 overflow-auto min-h-0">
              {currentAgents.length > 0 ? (
                <div className="space-y-4">
                  {currentAgents.map((agent) => (
                    <AgentRow key={agent.id} agent={agent} />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <div className="w-24 h-24 bg-gray-800 rounded-full flex items-center justify-center mb-6">
                    <Bot className="h-12 w-12 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-100 mb-2">
                    No AI Agents to Test Yet
                  </h3>
                  <p className="text-gray-400 mb-6 max-w-md">
                    Get started by testing your first AI agent. Connect to MCP
                    servers or REST APIs to begin comprehensive testing and
                    monitoring of your agents.
                  </p>
                  <Button
                    onClick={() => setIsTestModalOpen(true)}
                    className="bg-pink-400 hover:bg-pink-500 text-white"
                  >
                    <TestTube className="h-4 w-4 mr-2" />
                    Test Your First Agent
                  </Button>
                </div>
              )}
            </div>

            {/* Pagination - Always visible at bottom */}
            {totalPages > 1 && (
              <div className="border-t border-gray-700 bg-gray-800 px-4 lg:px-6 py-4 flex-shrink-0">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                  <div className="text-sm text-gray-400">
                    Showing {startIndex + 1} to{" "}
                    {Math.min(endIndex, filteredAgents.length)} of{" "}
                    {filteredAgents.length} results
                  </div>

                  <div className="flex items-center justify-center sm:justify-end space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      className="border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50"
                    >
                      <ChevronsLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>

                    <div className="flex items-center space-x-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                        (page) => (
                          <Button
                            key={page}
                            variant={
                              page === currentPage ? "default" : "outline"
                            }
                            size="sm"
                            onClick={() => setCurrentPage(page)}
                            className={
                              page === currentPage
                                ? "bg-pink-400 hover:bg-pink-500 text-white"
                                : "border-gray-600 text-gray-300 hover:bg-gray-700"
                            }
                          >
                            {page}
                          </Button>
                        ),
                      )}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                      className="border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50"
                    >
                      <ChevronsRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Test Agent Modal */}
      {isTestModalOpen && (
        <TestAgentModal
          isOpen={isTestModalOpen}
          onClose={() => setIsTestModalOpen(false)}
          onSuccess={handleAddAgent}
        />
      )}
    </div>
  );
};
